/**
 * The templates package's starter suggestions (`registry.ts`): on a
 * template's page, testing its wizard and reviewing its questions; on the
 * template list (`template_document_read`), listing subscribed templates and
 * counting templates by trade; on the own-templates list, composing a
 * template — each backed by the package's template tools. The own-templates
 * list is reached through a navigation entry gated by
 * `template_document_update`; the route itself is not gated, so a user
 * without that permission who opens `/templates/documents/my` directly may
 * still see the bathroom-template starter, and `compose_template` then
 * refuses it.
 */
import type { PageContext } from "../pageContext.js";
import { defineAgentStarter, onRoutes } from "./agentStarters.js";

const onTemplatePage = (context: PageContext) =>
  context.templateId !== undefined;

/**
 * The template list only — `onRoutes` matches the exact path, so a template's
 * page or the own-templates list never fits. Open to every user with
 * `template_document_read`, so it carries only read starters.
 */
const onTemplateList = onRoutes("/templates/documents/all");

/**
 * The own-templates list only — `onRoutes` matches the exact path, so a
 * template's page or the template list never fits. The list is reached through
 * a navigation entry gated by `template_document_update`, which
 * `compose_template` needs; the route itself is not gated, so a user without
 * that permission who opens `/templates/documents/my` directly may still see
 * the bathroom-template starter, and `compose_template` then refuses it.
 */
const onOwnTemplatesList = onRoutes("/templates/documents/my");

export const templatesAgentStarters = [
  defineAgentStarter({
    id: "templates.test-wizard",
    ns: "Assistant",
    textKey: "Test the wizard of this template",
    order: 10,
    match: onTemplatePage,
    requiredCapabilities: ["templating"],
  }),
  defineAgentStarter({
    id: "templates.review-questions",
    ns: "Assistant",
    textKey: "Check this template's questions",
    order: 20,
    match: onTemplatePage,
    requiredCapabilities: ["templating"],
  }),
  defineAgentStarter({
    id: "templates.bathroom-template",
    ns: "Assistant",
    textKey: "Create a template for a small bathroom renovation with …",
    order: 10,
    match: onOwnTemplatesList,
    requiredCapabilities: ["templating"],
  }),
  defineAgentStarter({
    id: "templates.subscribed",
    ns: "Assistant",
    textKey: "Show me all subscribed templates",
    order: 20,
    match: onTemplateList,
    requiredCapabilities: ["templating"],
  }),
  defineAgentStarter({
    id: "templates.count-by-trade",
    ns: "Assistant",
    textKey: "How are my templates spread across trades?",
    order: 30,
    match: onTemplateList,
    requiredCapabilities: ["templating"],
  }),
];
